/**
 * SalonHub デバイス認証・フィンガープリント強化システム
 * エンタープライズレベル デバイスセキュリティ管理
 *
 * @description 高度なデバイス認証・異常検出・信頼度管理
 * @security デバイス脅威検出・フィンガープリント分析
 * @integration crypto.ts・enhancedJWT.ts統合
 */

import { generateDataHash, generateDeviceFingerprint as baseFingerprintGenerator } from './crypto';
import logger from './logger';

// デバイス認証エラー
export class DeviceAuthError extends Error {
  constructor(
    message: string,
    public code: 'DEVICE_BLOCKED' | 'SUSPICIOUS_DEVICE' | 'FINGERPRINT_MISMATCH' | 'TRUST_THRESHOLD_FAILED'
  ) {
    super(message);
    this.name = 'DeviceAuthError';
  }
}

// デバイス特性情報
export interface DeviceCharacteristics {
  // 基本情報
  userAgent: string;
  language: string;
  languages: string[];
  platform: string;

  // ハードウェア特性
  hardwareConcurrency: number;
  maxTouchPoints: number;
  cookieEnabled: boolean;

  // 画面・表示特性
  screenWidth: number;
  screenHeight: number;
  colorDepth: number;
  pixelDepth: number;
  devicePixelRatio: number;
  screen?: {
    width: number;
    height: number;
    colorDepth: number;
    pixelDepth: number;
  };

  // 時間・地理特性
  timezoneOffset: number;
  timezone: string;

  // 高度な特性
  webGLRenderer?: string;
  webGLVendor?: string;
  canvasFingerprint?: string;
  audioFingerprint?: string;
  fontsFingerprint?: string;

  // 接続情報
  connectionType?: string;
  downlinkSpeed?: number;

  // セキュリティ特性
  doNotTrack?: string;
  adBlockDetected?: boolean;
  devToolsOpen?: boolean;
}

// デバイス信頼度プロファイル
export interface DeviceTrustProfile {
  deviceId: string;
  fingerprint: string;
  trustScore: number; // 0-100
  riskLevel: 'low' | 'medium' | 'high' | 'critical';

  // 利用履歴
  firstSeen: number;
  lastSeen: number;
  visitCount: number;
  successfulAuthCount: number;
  failedAuthCount: number;

  // 地理・接続情報
  ipAddresses: string[];
  countries: string[];
  isps: string[];

  // 異常フラグ
  anomalies: DeviceAnomaly[];
  isBlocked: boolean;
  blockReason?: string;
  blockTimestamp?: number;

  // メタデータ
  userIds: string[];
  tenantIds: string[];
  lastUpdated: number;
}

// デバイス異常情報
export interface DeviceAnomaly {
  type: 'location_change' | 'characteristics_change' | 'suspicious_timing' | 'multiple_users' | 'automation_detected';
  description: string;
  severity: 'low' | 'medium' | 'high' | 'critical';
  detectedAt: number;
  details: Record<string, unknown>;
}

// デバイス認証結果
export interface DeviceAuthResult {
  success: boolean;
  allowed: boolean;
  deviceId: string;
  trustScore: number;
  riskLevel: string;
  anomalies: DeviceAnomaly[];
  recommendations: string[];
  requiresAdditionalAuth?: boolean;
  isNewDevice?: boolean;
}

/**
 * 高度なデバイス認証サービス
 */
export class DeviceAuthService {
  private static readonly STORAGE_KEY = 'salonhub_device_profiles';
  private static readonly TRUST_THRESHOLD = 70; // 最低信頼度
  private static readonly BLOCK_THRESHOLD = 30; // ブロック閾値

  /**
   * 包括的デバイス特性収集
   * @returns デバイス特性
   */
  static collectDeviceCharacteristics(): DeviceCharacteristics {
    try {
      const characteristics: DeviceCharacteristics = {
        // 基本情報
        userAgent: navigator.userAgent,
        language: navigator.language,
        languages: navigator.languages ? Array.from(navigator.languages) : [],
        platform: navigator.platform,

        // ハードウェア特性
        hardwareConcurrency: navigator.hardwareConcurrency || 0,
        maxTouchPoints: navigator.maxTouchPoints || 0,
        cookieEnabled: navigator.cookieEnabled,

        // 画面・表示特性
        screenWidth: screen.width,
        screenHeight: screen.height,
        colorDepth: screen.colorDepth,
        pixelDepth: screen.pixelDepth || screen.colorDepth,
        devicePixelRatio: window.devicePixelRatio || 1,
        screen: {
          width: screen.width,
          height: screen.height,
          colorDepth: screen.colorDepth,
          pixelDepth: screen.pixelDepth || screen.colorDepth,
        },

        // 時間・地理特性
        timezoneOffset: new Date().getTimezoneOffset(),
        timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,

        // 高度な特性
        webGLRenderer: this.getWebGLRenderer(),
        webGLVendor: this.getWebGLVendor(),
        canvasFingerprint: this.getCanvasFingerprint(),
        audioFingerprint: this.getAudioFingerprint(),
        fontsFingerprint: this.getFontsFingerprint(),

        // 接続情報
        connectionType: this.getConnectionType(),
        downlinkSpeed: this.getDownlinkSpeed(),

        // セキュリティ特性
        doNotTrack: navigator.doNotTrack || undefined,
        adBlockDetected: this.detectAdBlock(),
        devToolsOpen: this.detectDevTools(),
      };

      return characteristics;
    } catch (error) {
      logger.error('デバイス特性収集エラー:', error);
      throw new DeviceAuthError('デバイス特性の収集に失敗しました', 'FINGERPRINT_MISMATCH');
    }
  }

  /**
   * 高度なデバイスフィンガープリント生成
   * @param characteristics デバイス特性
   * @returns 高精度フィンガープリント
   */
  static generateAdvancedFingerprint(characteristics: DeviceCharacteristics): string {
    try {
      // 重要度による重み付け
      const components = [
        `ua:${characteristics.userAgent}`,
        `lang:${characteristics.language}`,
        `langs:${characteristics.languages.join(',')}`,
        `platform:${characteristics.platform}`,
        `hw:${characteristics.hardwareConcurrency}`,
        `touch:${characteristics.maxTouchPoints}`,
        `cookie:${characteristics.cookieEnabled}`,
        `screen:${characteristics.screenWidth}x${characteristics.screenHeight}`,
        `color:${characteristics.colorDepth}`,
        `pixel:${characteristics.pixelDepth}`,
        `dpr:${characteristics.devicePixelRatio}`,
        `tz:${characteristics.timezoneOffset}`,
        `timezone:${characteristics.timezone}`,
        `webgl:${characteristics.webGLRenderer || 'unknown'}:${characteristics.webGLVendor || 'unknown'}`,
        `canvas:${characteristics.canvasFingerprint || 'unknown'}`,
        `audio:${characteristics.audioFingerprint || 'unknown'}`,
        `fonts:${characteristics.fontsFingerprint || 'unknown'}`,
        `conn:${characteristics.connectionType || 'unknown'}`,
        `speed:${characteristics.downlinkSpeed || 0}`,
        `dnt:${characteristics.doNotTrack || 'unknown'}`,
        `adblock:${characteristics.adBlockDetected}`,
        `devtools:${characteristics.devToolsOpen}`,
      ];

      const fingerprintData = components.join('|');
      return generateDataHash(fingerprintData);
    } catch (error) {
      logger.error('高度フィンガープリント生成エラー:', error);
      return baseFingerprintGenerator(); // フォールバック
    }
  }

  /**
   * デバイス認証実行
   * @param userId ユーザーID
   * @param characteristics デバイス特性
   * @param ipAddress IPアドレス（オプション）
   * @returns 認証結果
   */
  static async authenticateDevice(
    userId: string,
    characteristics: DeviceCharacteristics,
    ipAddress?: string
  ): Promise<DeviceAuthResult> {
    try {
      const fingerprint = this.generateAdvancedFingerprint(characteristics);
      const deviceId = generateDataHash(fingerprint.substring(0, 16));

      // 既存プロファイル取得
      let profile = await this.getDeviceProfile(deviceId);

      if (!profile) {
        // 新規デバイス
        profile = this.createNewDeviceProfile(deviceId, fingerprint, userId, characteristics, ipAddress);
      } else {
        // 既存デバイス更新
        profile = await this.updateDeviceProfile(profile, userId, characteristics, ipAddress, fingerprint);
      }

      // 異常検出
      const anomalies = await this.detectAnomalies(profile, characteristics, ipAddress);
      profile.anomalies = [...profile.anomalies, ...anomalies];

      // 信頼度計算
      const trustScore = this.calculateTrustScore(profile, anomalies);
      profile.trustScore = trustScore;

      // リスクレベル決定
      const riskLevel = this.determineRiskLevel(trustScore, anomalies);
      profile.riskLevel = riskLevel;

      // ブロック判定
      if (trustScore < this.BLOCK_THRESHOLD || anomalies.some(a => a.severity === 'critical')) {
        profile.isBlocked = true;
        profile.blockReason = `信頼度不足(${trustScore}) または重大な異常検出`;
        profile.blockTimestamp = Date.now();
      }

      // プロファイル保存
      await this.saveDeviceProfile(profile);

      // 認証結果構築
      const authResult: DeviceAuthResult = {
        success: true,
        allowed: !profile.isBlocked && trustScore >= this.TRUST_THRESHOLD,
        deviceId,
        trustScore,
        riskLevel,
        anomalies,
        recommendations: this.generateRecommendations(profile, anomalies),
        requiresAdditionalAuth: trustScore < this.TRUST_THRESHOLD,
        isNewDevice: profile.visitCount === 1,
      };

      logger.info(`デバイス認証完了: ${deviceId} (信頼度: ${trustScore}, リスク: ${riskLevel}, 許可: ${authResult.allowed})`);
      return authResult;
    } catch (error) {
      logger.error('デバイス認証エラー:', error);


      throw new DeviceAuthError(
        `デバイス認証に失敗しました: ${error instanceof Error ? error.message : 'Unknown error'}`,
        'DEVICE_BLOCKED'
      );
    }
  }

  /**
   * デバイスブロック解除
   * @param deviceId デバイスID
   * @param reason 解除理由
   * @returns 解除結果
   */
  static async unblockDevice(deviceId: string, reason: string): Promise<boolean> {
    try {
      const profile = await this.getDeviceProfile(deviceId);
      if (!profile) {
        return false;
      }

      profile.isBlocked = false;
      profile.blockReason = undefined;
      profile.blockTimestamp = undefined;
      profile.trustScore = Math.min(profile.trustScore + 20, 100); // 信頼度を20ポイント回復

      await this.saveDeviceProfile(profile);
      logger.info(`デバイスブロック解除: ${deviceId} (理由: ${reason})`);
      return true;
    } catch (error) {
      logger.error('デバイスブロック解除エラー:', error);
      return false;
    }
  }

  // ================================================================
  // プライベート・ヘルパーメソッド
  // ================================================================

  /**
   * WebGLレンダラー取得
   */
  private static getWebGLRenderer(): string {
    try {
      const canvas = document.createElement('canvas');
      const gl = canvas.getContext('webgl') || canvas.getContext('experimental-webgl') as WebGLRenderingContext;
      if (!gl) return 'unknown';

      const debugInfo = gl.getExtension('WEBGL_debug_renderer_info');
      if (debugInfo) {
        return gl.getParameter(debugInfo.UNMASKED_RENDERER_WEBGL) || 'unknown';
      }
      return gl.getParameter(gl.RENDERER) || 'unknown';
    } catch (error) {
      return 'error';
    }
  }

  /**
   * WebGLベンダー取得
   */
  private static getWebGLVendor(): string {
    try {
      const canvas = document.createElement('canvas');
      const gl = canvas.getContext('webgl') || canvas.getContext('experimental-webgl') as WebGLRenderingContext;
      if (!gl) return 'unknown';

      const debugInfo = gl.getExtension('WEBGL_debug_renderer_info');
      if (debugInfo) {
        return gl.getParameter(debugInfo.UNMASKED_VENDOR_WEBGL) || 'unknown';
      }
      return gl.getParameter(gl.VENDOR) || 'unknown';
    } catch (error) {
      return 'error';
    }
  }

  /**
   * Canvas フィンガープリント生成
   */
  private static getCanvasFingerprint(): string {
    try {
      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d');
      if (!ctx) return 'unknown';

      // 複雑な描画パターン
      ctx.textBaseline = 'top';
      ctx.font = '14px Arial';
      ctx.fillStyle = '#f60';
      ctx.fillRect(125, 1, 62, 20);
      ctx.fillStyle = '#069';
      ctx.fillText('SalonHub🔒', 2, 15);
      ctx.fillStyle = 'rgba(102, 204, 0, 0.7)';
      ctx.fillText('Device Auth', 4, 17);

      return generateDataHash(canvas.toDataURL()).substring(0, 16);
    } catch (error) {
      return 'error';
    }
  }

  /**
   * Audio フィンガープリント生成
   */
  private static getAudioFingerprint(): string {
    try {
      // AudioContextによる音響フィンガープリント（軽量版）
      const audioContext = new (window.AudioContext || (window as typeof window & { webkitAudioContext?: typeof AudioContext }).webkitAudioContext)();
      const oscillator = audioContext.createOscillator();
      const analyser = audioContext.createAnalyser();

      oscillator.connect(analyser);
      oscillator.frequency.value = 1000;

      const frequencyData = new Uint8Array(analyser.frequencyBinCount);
      analyser.getByteFrequencyData(frequencyData);

      audioContext.close();

      return generateDataHash(Array.from(frequencyData).join(',')).substring(0, 16);
    } catch (error) {
      return 'error';
    }
  }

  /**
   * フォント フィンガープリント生成
   */
  private static getFontsFingerprint(): string {
    try {
      const testFonts = ['Calibri', 'Cambria', 'Georgia', 'Palatino', 'Trebuchet MS'];

      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d');
      if (!ctx) return 'unknown';

      const detected: string[] = [];

      testFonts.forEach(font => {
        ctx.font = `72px ${font}, serif`;
        const width = ctx.measureText('SalonHub').width;

        ctx.font = '72px serif';
        const serifWidth = ctx.measureText('SalonHub').width;

        if (width !== serifWidth) {
          detected.push(font);
        }
      });

      return generateDataHash(detected.join(',')).substring(0, 16);
    } catch (error) {
      return 'error';
    }
  }

  /**
   * 接続タイプ取得
   */
  private static getConnectionType(): string {
    try {
      const connection = (navigator as Navigator & {
        connection?: { effectiveType?: string };
        mozConnection?: { effectiveType?: string };
        webkitConnection?: { effectiveType?: string };
      }).connection || (navigator as Navigator & {
        connection?: { effectiveType?: string };
        mozConnection?: { effectiveType?: string };
        webkitConnection?: { effectiveType?: string };
      }).mozConnection || (navigator as Navigator & {
        connection?: { effectiveType?: string };
        mozConnection?: { effectiveType?: string };
        webkitConnection?: { effectiveType?: string };
      }).webkitConnection;
      return connection?.effectiveType || 'unknown';
    } catch (error) {
      return 'unknown';
    }
  }

  /**
   * 回線速度取得
   */
  private static getDownlinkSpeed(): number {
    try {
      const connection = (navigator as Navigator & {
        connection?: { downlink?: number };
        mozConnection?: { downlink?: number };
        webkitConnection?: { downlink?: number };
      }).connection || (navigator as Navigator & {
        connection?: { downlink?: number };
        mozConnection?: { downlink?: number };
        webkitConnection?: { downlink?: number };
      }).mozConnection || (navigator as Navigator & {
        connection?: { downlink?: number };
        mozConnection?: { downlink?: number };
        webkitConnection?: { downlink?: number };
      }).webkitConnection;
      return connection?.downlink || 0;
    } catch (error) {
      return 0;
    }
  }

  /**
   * 広告ブロック検出
   */
  private static detectAdBlock(): boolean {
    try {
      const ad = document.createElement('div');
      ad.innerHTML = '&nbsp;';
      ad.className = 'adsbox';
      ad.style.position = 'absolute';
      ad.style.left = '-999px';
      document.body.appendChild(ad);

      const blocked = ad.offsetHeight === 0;
      document.body.removeChild(ad);

      return blocked;
    } catch (error) {
      return false;
    }
  }

  /**
   * 開発者ツール検出
   */
  private static detectDevTools(): boolean {
    try {
      const threshold = 160;
      return (window.outerHeight - window.innerHeight > threshold) ||
             (window.outerWidth - window.innerWidth > threshold);
    } catch (error) {
      return false;
    }
  }

  /**
   * 新規デバイスプロファイル作成
   */
  private static createNewDeviceProfile(
    deviceId: string,
    fingerprint: string,
    userId: string,
    _characteristics: DeviceCharacteristics,
    ipAddress?: string
  ): DeviceTrustProfile {
    const now = Date.now();

    return {
      deviceId,
      fingerprint,
      trustScore: 50, // 初期信頼度
      riskLevel: 'medium',
      firstSeen: now,
      lastSeen: now,
      visitCount: 1,
      successfulAuthCount: 0,
      failedAuthCount: 0,
      ipAddresses: ipAddress ? [ipAddress] : [],
      countries: [],
      isps: [],
      anomalies: [],
      isBlocked: false,
      userIds: [userId],
      tenantIds: [],
      lastUpdated: now,
    };
  }

  /**
   * デバイスプロファイル更新
   */
  private static async updateDeviceProfile(
    profile: DeviceTrustProfile,
    userId: string,
    _characteristics: DeviceCharacteristics,
    ipAddress?: string,
    newFingerprint?: string
  ): Promise<DeviceTrustProfile> {
    const now = Date.now();

    // 基本情報更新
    profile.lastSeen = now;
    profile.visitCount += 1;
    profile.lastUpdated = now;

    // ユーザー追加
    if (!profile.userIds.includes(userId)) {
      profile.userIds.push(userId);
    }

    // IPアドレス追加
    if (ipAddress && !profile.ipAddresses.includes(ipAddress)) {
      profile.ipAddresses.push(ipAddress);
      // 最新10個まで保持
      profile.ipAddresses = profile.ipAddresses.slice(-10);
    }

    // フィンガープリント変更検出
    if (newFingerprint && profile.fingerprint !== newFingerprint) {
      profile.anomalies.push({
        type: 'characteristics_change',
        description: 'デバイス特性の変更が検出されました',
        severity: 'medium',
        detectedAt: now,
        details: { oldFingerprint: profile.fingerprint, newFingerprint }
      });
      profile.fingerprint = newFingerprint;
    }

    return profile;
  }

  /**
   * 異常検出
   */
  private static async detectAnomalies(
    profile: DeviceTrustProfile,
    characteristics: DeviceCharacteristics,
    _ipAddress?: string
  ): Promise<DeviceAnomaly[]> {
    const anomalies: DeviceAnomaly[] = [];
    const now = Date.now();

    // 複数ユーザー検出
    if (profile.userIds.length > 3) {
      anomalies.push({
        type: 'multiple_users',
        description: `複数ユーザー(${profile.userIds.length}人)によるデバイス使用を検出`,
        severity: 'high',
        detectedAt: now,
        details: { userCount: profile.userIds.length }
      });
    }

    // 自動化検出
    if (characteristics.devToolsOpen) {
      anomalies.push({
        type: 'automation_detected',
        description: '開発者ツールが開かれています',
        severity: 'medium',
        detectedAt: now,
        details: { devToolsOpen: true }
      });
    }

    // 短時間での大量アクセス
    const recentVisits = profile.visitCount;
    const timeSinceFirst = now - profile.firstSeen;
    const visitRate = recentVisits / (timeSinceFirst / (60 * 60 * 1000)); // visits per hour

    if (visitRate > 100) { // 1時間に100回以上
      anomalies.push({
        type: 'suspicious_timing',
        description: `異常なアクセス頻度: ${visitRate.toFixed(1)}回/時`,
        severity: 'high',
        detectedAt: now,
        details: { visitRate, recentVisits, timeWindow: timeSinceFirst }
      });
    }

    return anomalies;
  }

  /**
   * 信頼度計算
   */
  private static calculateTrustScore(profile: DeviceTrustProfile, newAnomalies: DeviceAnomaly[]): number {
    let score = profile.trustScore;

    // 利用実績による加点
    const daysSinceFirst = (Date.now() - profile.firstSeen) / (24 * 60 * 60 * 1000);
    score += Math.min(daysSinceFirst * 2, 20); // 最大20点

    // 成功率による加点
    const totalAuth = profile.successfulAuthCount + profile.failedAuthCount;
    if (totalAuth > 0) {
      const successRate = profile.successfulAuthCount / totalAuth;
      score += successRate * 20; // 最大20点
    }

    // 異常による減点
    newAnomalies.forEach(anomaly => {
      switch (anomaly.severity) {
        case 'critical': score -= 30; break;
        case 'high': score -= 20; break;
        case 'medium': score -= 10; break;
        case 'low': score -= 5; break;
      }
    });

    return Math.max(0, Math.min(100, score));
  }

  /**
   * リスクレベル決定
   */
  private static determineRiskLevel(trustScore: number, anomalies: DeviceAnomaly[]): 'low' | 'medium' | 'high' | 'critical' {
    if (anomalies.some(a => a.severity === 'critical') || trustScore < 30) {
      return 'critical';
    }
    if (anomalies.some(a => a.severity === 'high') || trustScore < 50) {
      return 'high';
    }
    if (anomalies.some(a => a.severity === 'medium') || trustScore < 70) {
      return 'medium';
    }
    return 'low';
  }

  /**
   * 推奨事項生成
   */
  private static generateRecommendations(profile: DeviceTrustProfile, anomalies: DeviceAnomaly[]): string[] {
    const recommendations: string[] = [];

    if (profile.trustScore < this.TRUST_THRESHOLD) {
      recommendations.push('追加認証（SMS・メール）の実行を推奨');
    }

    if (anomalies.some(a => a.type === 'multiple_users')) {
      recommendations.push('このデバイスは複数ユーザーで共有されている可能性があります');
    }

    if (anomalies.some(a => a.type === 'automation_detected')) {
      recommendations.push('自動化ツールの使用が検出されました。手動操作を確認してください');
    }

    if (profile.visitCount === 1) {
      recommendations.push('新しいデバイスです。初回利用の確認を推奨');
    }

    return recommendations;
  }

  /**
   * デバイスプロファイル取得
   */
  private static async getDeviceProfile(deviceId: string): Promise<DeviceTrustProfile | null> {
    try {
      const profilesStr = localStorage.getItem(this.STORAGE_KEY) || '{}';
      const profiles: Record<string, DeviceTrustProfile> = JSON.parse(profilesStr);
      return profiles[deviceId] || null;
    } catch (error) {
      logger.error('デバイスプロファイル取得エラー:', error);
      return null;
    }
  }

  /**
   * デバイスプロファイル保存
   */
  private static async saveDeviceProfile(profile: DeviceTrustProfile): Promise<void> {
    try {
      const profilesStr = localStorage.getItem(this.STORAGE_KEY) || '{}';
      const profiles: Record<string, DeviceTrustProfile> = JSON.parse(profilesStr);
      profiles[profile.deviceId] = profile;
      localStorage.setItem(this.STORAGE_KEY, JSON.stringify(profiles));
    } catch (error) {
      logger.error('デバイスプロファイル保存エラー:', error);
    }
  }
}

/**
 * デバイス認証情報取得（デバッグ用）
 */
export const getDeviceAuthInfo = () => {
  if (import.meta.env.PROD) {
    return { error: 'Production環境では利用できません' };
  }

  return {
    implementation: '高度デバイスフィンガープリント認証',
    features: [
      'WebGL・Canvas・Audio フィンガープリント',
      'デバイス信頼度スコアリング',
      '異常行動検出',
      '自動化ツール検出',
      'アダプティブ認証',
      'デバイス履歴管理'
    ],
    trustThreshold: DeviceAuthService['TRUST_THRESHOLD'],
    blockThreshold: DeviceAuthService['BLOCK_THRESHOLD'],
    detectionCapabilities: [
      '複数ユーザー共有検出',
      '地理的移動異常',
      'ブラウザ特性変更',
      '開発者ツール使用',
      '広告ブロック検出',
      '異常アクセスパターン'
    ],
    timestamp: new Date().toISOString(),
  };
};

// 開発環境でのテスト用エクスポート
if (import.meta.env.DEV) {
  (window as typeof window & { SalonHubDeviceAuth: object }).SalonHubDeviceAuth = {
    service: DeviceAuthService,
    collect: DeviceAuthService.collectDeviceCharacteristics,
    fingerprint: DeviceAuthService.generateAdvancedFingerprint,
    getInfo: getDeviceAuthInfo,
  };
}